/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useApi, featureFlagsApiRef } from '@backstage/frontend-plugin-api';

import { HomePage } from './HomePage';
import { CustomizableHomePage } from './CustomizableHomePage';

/**
 * Feature-flag name controlling the customizable home page preview.
 *
 * Ported from the legacy app where the home route was wrapped in
 * `FeatureFlagged with="customizable-home-page-preview"` (customizable
 * variant) / `without` (default variant).
 */
export const CUSTOMIZABLE_HOME_PAGE_FLAG = 'customizable-home-page-preview';

/**
 * Home page root that selects the home variant based on the
 * `customizable-home-page-preview` feature flag, replicating the legacy
 * `App.tsx` behavior in the new frontend system.
 */
export const HomeRoot = () => {
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const customizable = featureFlagsApi.isActive(CUSTOMIZABLE_HOME_PAGE_FLAG);

  return customizable ? <CustomizableHomePage /> : <HomePage />;
};
